import { useEffect, useRef } from 'react'
import { buildStatColorExpression } from '../../utils/colors'
import { resolveOwnLayer } from '../../utils/regionStats'
import { buildWeatherColorExpression } from '../../utils/weather'
import { usePrefsStore } from '../../store/prefsStore'
import { resolvePalette, samplePalette } from '../../utils/palettes'
import { computeBreaks, buildStepExpression } from '../../utils/classify'
import { CONTINUOUS_LAYERS, NO_DATA_COLOR, activeContinuousKey } from '../../utils/choropleth'
import { buildDiplomacyColorExpression } from '../../utils/diplomacyData'
import { WAR_COLORS } from '../../utils/wars'

// Paint the regions-fill layer for the active visualization (radio-style: only
// one at a time). Plan Mode overrides everything via per-feature state (diffed
// against the previous colors for cheap GPU updates); otherwise the active base
// layer / owned-country heatmap picks the fill-color expression.
// `previousCustomColorsRef` is shared with MapView's click handler, so it's owned
// there and passed in; the last-expression cache is private to this hook.
export function useMapLayers(map, previousCustomColorsRef, {
  regionsData, planMode, customColors, layers, statType, regionStats,
  organizationsCatalog, selectedOrgId, wars, selectedWarId, diplomacyData,
}) {
  const lastPaintExpressionRef = useRef(null)
  const palette = usePrefsStore((s) => s.palette)
  const classification = usePrefsStore((s) => s.classification)
  const nClasses = usePrefsStore((s) => s.nClasses)
  const customRamp = usePrefsStore((s) => s.customRamp)

  useEffect(() => {
    if (!map.current || !regionsData || !map.current.getSource('regions')) return

    // Helper function to convert customColors object to Map (areas already resolved in App)
    const toRegionColorsMap = (colorEntries) => {
      const regionColors = new Map()
      colorEntries.forEach(([key, color]) => {
        regionColors.set(parseInt(key), color)
      })
      return regionColors
    }

    // Helper to apply paint property only if changed (avoids GPU recalc)
    const setPaintPropertyIfChanged = (propertyName, expression) => {
      const expressionKey = JSON.stringify(expression)
      if (lastPaintExpressionRef.current !== expressionKey) {
        map.current.setPaintProperty(propertyName, 'fill-color', expression)
        lastPaintExpressionRef.current = expressionKey
      }
    }

    // Plan Mode: Use custom colors (overrides other layers)
    if (planMode) {
      if (Object.keys(customColors).length > 0) {
        // OPTIMIZED: Only update changed feature states (diff-based)
        const previousColors = previousCustomColorsRef.current

        // Build current and previous region -> color maps (areas pre-resolved in App)
        const currentRegionColors = toRegionColorsMap(Object.entries(customColors))
        const previousRegionColors = toRegionColorsMap(Object.entries(previousColors))

        // Find regions that were removed (need to clear state)
        previousRegionColors.forEach((_, regionId) => {
          if (!currentRegionColors.has(regionId)) {
            try {
              map.current.removeFeatureState({
                source: 'regions',
                id: regionId
              })
            } catch (e) { /* ignore */ }
          }
        })

        // Find regions that were added or changed (need to set state)
        currentRegionColors.forEach((color, regionId) => {
          const prevColor = previousRegionColors.get(regionId)
          if (prevColor !== color) {
            try {
              map.current.setFeatureState(
                { source: 'regions', id: regionId },
                { customColor: color }
              )
            } catch (e) { /* ignore */ }
          }
        })

        // Update reference for next diff
        previousCustomColorsRef.current = { ...customColors }

        // Use a simple expression that reads from feature-state (only update if needed)
        const planExpression = [
          'case',
          ['!=', ['feature-state', 'customColor'], null],
          ['feature-state', 'customColor'],
          '#cccccc'
        ]
        setPaintPropertyIfChanged('regions-fill', planExpression)
      } else {
        // Clear all feature states only if we had colors before
        if (Object.keys(previousCustomColorsRef.current).length > 0) {
          // Get all region IDs to clear them
          const previousRegionColors = toRegionColorsMap(Object.entries(previousCustomColorsRef.current))
          previousRegionColors.forEach((_, regionId) => {
            try {
              map.current.removeFeatureState({
                source: 'regions',
                id: regionId
              })
            } catch (e) { /* ignore */ }
          })
          previousCustomColorsRef.current = {}
        }
        setPaintPropertyIfChanged('regions-fill', '#cccccc')
      }
    }
    // Normal layer rendering (not in Plan Mode)
    else if (layers.countries) {
      // Country colors
      const colorExpression = [
        'case',
        ['==', ['get', 'country_id'], null], '#888888', // Unoccupied (darker gray)
        ['has', 'country_color'], ['get', 'country_color'], // Use country color
        '#cccccc' // Fallback
      ]
      setPaintPropertyIfChanged('regions-fill', colorExpression)
    } else if (layers.playedCountries) {
      // Played Countries Mode: Grey (no country), Green (unplayed - overrides inactive), Orange (played but inactive), Red (played & active)
      const colorExpression = [
        'case',
        ['==', ['get', 'country_id'], null], '#888888', // No country assigned
        ['!=', ['get', 'is_played'], true], '#4CAF50', // Unplayed country (green) - takes priority over inactive status
        // Now check for inactive (only relevant for PLAYED countries)
        [
          'any',
          ['==', ['get', 'months_since_activity'], null], // No activity data
          ['>', ['get', 'months_since_activity'], 5] // Inactive for >5 months
        ],
        '#FFA500', // Inactive PLAYED country (orange)
        '#E24A4A' // Active PLAYED country (red)
      ]
      setPaintPropertyIfChanged('regions-fill', colorExpression)
    } else if (layers.diplomacy) {
      setPaintPropertyIfChanged('regions-fill', buildDiplomacyColorExpression(diplomacyData))
    } else if (activeContinuousKey(layers)) {
      // Smart choropleth (S12): data-driven class breaks + chosen palette (S11),
      // so skewed distributions still use the full ramp instead of one flat colour.
      const def = CONTINUOUS_LAYERS[activeContinuousKey(layers)]
      const colors = samplePalette(resolvePalette(palette, customRamp), nClasses)
      const values = (regionsData.features || []).map((f) => def.value(f.properties))
      const breaks = computeBreaks(values, classification, nClasses)
      const ramped = breaks.length
        ? buildStepExpression(def.expr, breaks, colors)
        : colors[colors.length - 1]
      // Regions the metric is undefined for get the neutral colour, never the
      // ramp's lowest class — "no country" is not "poorest country".
      const expr = def.nodata ? ['case', def.nodata, NO_DATA_COLOR, ramped] : ramped
      setPaintPropertyIfChanged('regions-fill', expr)
    } else if (layers.climate) {
      // Climate zones - all 11 specific types
      const colorExpression = [
        'match',
        ['get', 'climate'],
        'Tempéré Hiver sec', '#A9E55F',
        'Tempéré Été sec', '#C6483F',
        'Tempéré Humide', '#95B881',
        'Tropical Humide et Sec', '#89C342',
        'Tropical Humide', '#378E2A',
        'Aride', '#E8BD68',
        'Désertique', '#FEFFB3',
        'Continental Sec', '#B89D67',
        'Continental Humide', '#D3E5C9',
        'Montagne', '#93408D',
        'Arctique', '#6593DC',
        '#CCCCCC' // Unknown
      ]
      setPaintPropertyIfChanged('regions-fill', colorExpression)
    } else if (layers.topography) {
      // Topography: the terrain raster does the painting; regions go almost
      // fully transparent (kept for click/hover interactions and borders).
      setPaintPropertyIfChanged('regions-fill', '#cccccc')
    } else if (layers.weather) {
      // Live weather — colored by the weather_type feature property merged in
      // from the /map/weather snapshot (see the weatherData merge effect below).
      setPaintPropertyIfChanged('regions-fill', buildWeatherColorExpression())
    } else if (layers.hemisphere) {
      // Hemisphere colors
      const colorExpression = [
        'match',
        ['get', 'hemisphere'],
        'north', '#4A90E2',
        'south', '#E24A4A',
        'equator', '#D5B654',
        '#888888' // Unknown
      ]
      setPaintPropertyIfChanged('regions-fill', colorExpression)
    } else if (layers.wars) {
      // Qui se bat contre qui : l'attaquant dans une couleur, le défenseur dans
      // l'autre, tout le reste neutre. Deux teintes opposées et jamais deux
      // rouges — la carte doit répondre « qui contre qui » d'un coup d'œil, et
      // une opposition chaud/froid se distingue même en vision dichromatique.
      //
      // Le coloriage se fait par `country_id`, déjà présent sur chaque région :
      // rien à régénérer côté GeoJSON, donc une déclaration de guerre apparaît
      // sur la carte sans attendre le prochain passage du listener.
      const war = (wars || []).find((w) => w.id === selectedWarId)
      const colorExpression = !war
        ? '#cccccc'
        : [
            'case',
            ['==', ['coalesce', ['get', 'country_id'], -1], war.attacker], WAR_COLORS.attacker,
            ...(war.defender ? [['==', ['coalesce', ['get', 'country_id'], -1], war.defender], WAR_COLORS.defender] : []),
            ...(war.targetRegionId ? [['==', ['get', 'region_id'], war.targetRegionId], WAR_COLORS.defender] : []),
            WAR_COLORS.other,
          ]
      setPaintPropertyIfChanged('regions-fill', colorExpression)
    } else if (layers.organizations) {
      // Membership map for one selected organization: its members in the
      // org's color, everyone else neutral gray. organization_ids is a
      // per-feature int array baked in at GeoJSON generation time.
      const orgColor = (organizationsCatalog && selectedOrgId != null && organizationsCatalog[selectedOrgId]?.color) || '#4A90E2'
      const colorExpression = selectedOrgId == null
        ? '#cccccc'
        : [
            'case',
            ['in', selectedOrgId, ['coalesce', ['get', 'organization_ids'], ['literal', []]]], orgColor,
            '#3a3a3a',
          ]
      setPaintPropertyIfChanged('regions-fill', colorExpression)
    } else {
      // Owned-country heatmap layers (detector: only regions carrying stat_has
      // are colored along the ramp; all others render neutral).
      const ownLayer = resolveOwnLayer(layers, statType)
      if (ownLayer) {
        if (ownLayer.prop) {
          setPaintPropertyIfChanged('regions-fill', buildStatColorExpression(ownLayer.prop, ownLayer.ramp))
        } else {
          // Dynamic layer active but no type selected yet
          setPaintPropertyIfChanged('regions-fill', '#3a3a3a')
        }
      } else {
        // Default gray
        setPaintPropertyIfChanged('regions-fill', '#cccccc')
      }
    }
  }, [map, previousCustomColorsRef, layers, regionsData, planMode, customColors, statType, regionStats, organizationsCatalog, selectedOrgId, diplomacyData, palette, classification, nClasses, customRamp])
}
